import React, {
  FC,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import ChatSidebar from './sidebar/ChatSidebar';
import ChatHeader from './ChatHeader';
import ChatCanvas from './canvas/ChatCanvas';
import {
  ChatCanvasProvider,
  useChatCanvas,
} from '../../contexts/ChatCanvasContext';
import { SidebarDrawerProvider } from '../../contexts/SidebarDrawerContext';
import {
  ChatSidebarCollapseProvider,
  useChatSidebarCollapse,
} from '../../contexts/ChatSidebarCollapseContext';
import { ConfirmationsProvider } from '../../contexts/ConfirmationsContext';
import {
  SubThreadViewProvider,
  useSubThreadViewIsOpen,
} from '../../contexts/SubThreadViewContext';
import {
  HeroImageProvider,
  useHeroImage,
} from '../../contexts/HeroImageContext';
import SubThreadView from './sidebar/SubThreadView';
import { usePusher } from '../../contexts/PusherContext';
import { PUSHER_ENV } from '../../lib/pusherClient';
import SidebarDrawer from './sidebar/SidebarDrawer';
import { useMobileNav } from '../../contexts/MobileNavContext';
import api from '../../api';
import {
  ThreadLayoutType,
  GetThreadLayoutTypeResponse,
  AgentEvents,
  BoundProDoc,
  SubThreadInfo,
  SubThreadUpdateEvent,
  ProjectAppTab,
  ProjectAppCreatedEvent,
  TodoImageReadyEvent,
  threadChannel,
} from '@dreamai/types';
import { ProjectAppLoadingProvider } from '../../providers/ProjectAppViewLoadingProvider';
import { StatusPillHiddenProvider } from '../../contexts/StatusPillContext';
import { ReactComponent as ArrowLeftIcon } from '../../icons/arrow-left.svg';
import { ReactComponent as ProjectsFolderIcon } from '../../icons/projects-folder.svg';
import { ReactComponent as AlignRightIcon } from '../../icons/align-right-01.svg';
import { ReactComponent as ExpandIcon } from '../../icons/expand-01.svg';
import { useAgent } from '../../contexts/AgentContext';
import ProjectsDrawer from './ExpansiveSideNavigation/ProjectsDrawer';
import {
  ThreadCallProvider,
  useThreadCallOptional,
} from '../../contexts/ThreadCallContext';
import { ReactComponent as CallingIcon } from '../../icons/webrtc-calls/calling.svg';

interface ChatInterfaceContentProps {
  threadId: string | null;
}

const ChatTopBanner: FC = () => {
  const { heroImage } = useHeroImage();
  return (
    <div className={`chat-top-banner${heroImage ? ' has-hero-image' : ''}`}>
      {heroImage && (
        <>
          <div
            className='chat-top-banner-blur'
            style={{ backgroundImage: `url(${heroImage})` }}
          />
          <img
            className='chat-top-banner-image'
            src={heroImage}
            alt=''
            aria-hidden='true'
          />
        </>
      )}
    </div>
  );
};

const ChatInterfaceContent: FC<ChatInterfaceContentProps> = ({ threadId }) => {
  const { chatCanvasRef } = useChatCanvas();
  const { getAccessTokenSilently } = useAuth0();
  const pusherContext = usePusher(true);
  const { isCollapsed, collapseSidebar, expandSidebar } =
    useChatSidebarCollapse();
  const isSubThreadViewOpen = useSubThreadViewIsOpen();
  const threadCall = useThreadCallOptional();

  // Thread layout type state
  const [layoutType, setLayoutType] = useState<ThreadLayoutType>('tab');
  const [discussingObject, setDiscussingObject] = useState<
    string | undefined
  >();
  const [discussingObjectMatch, setDiscussingObjectMatch] = useState<
    Record<string, any> | undefined
  >();
  const [isMainThread, setIsMainThread] = useState(false);
  const [isChannelThread, setIsChannelThread] = useState(false);
  const [isOnboardingThread, setIsOnboardingThread] = useState(false);
  const [isInboundEmailThread, setIsInboundEmailThread] = useState(false);
  const [agentType, setAgentType] = useState<string | undefined>();
  const [boundProDocs, setBoundProDocs] = useState<BoundProDoc[]>([]);
  const [subThreads, setSubThreads] = useState<SubThreadInfo[]>([]);
  const [projectApps, setProjectApps] = useState<ProjectAppTab[]>([]);
  const [threadDescription, setThreadDescription] = useState<
    string | undefined
  >();
  const [parentThreadId, setParentThreadId] = useState<string | undefined>();
  const [parentThreadIsInternalMonologue, setParentThreadIsInternalMonologue] =
    useState<boolean | undefined>();
  const [iconImageUrl, setIconImageUrl] = useState<string | undefined>();
  const [layoutLoading, setLayoutLoading] = useState(true);

  const navigate = useNavigate();

  // Mobile sidebar state (from shared context)
  const { isMobile, openMobileNav, closeMobileNav } = useMobileNav();

  // Mobile canvas drawer state
  const [canvasDrawerOpen, setCanvasDrawerOpen] = useState(false);

  // Track if there are canvas assets
  const [hasCanvasAssets, setHasCanvasAssets] = useState(false);

  // Projects drawer (opened from the chat top bar)
  const { activeAgent } = useAgent();
  const [projectsDrawerOpen, setProjectsDrawerOpen] = useState(false);
  const allProjectsBtnRef = useRef<HTMLButtonElement>(null);

  // Fetch layout type for the current thread
  const fetchLayoutType = useCallback(async () => {
    setLayoutLoading(true);
    // Reset layout state immediately so stale values from the previous thread
    // don't persist while the async fetch is in-flight.
    setLayoutType('tab');
    setDiscussingObject(undefined);
    setDiscussingObjectMatch(undefined);
    setIsMainThread(false);
    setIsChannelThread(false);
    setIsOnboardingThread(false);
    setIsInboundEmailThread(false);
    setAgentType(undefined);
    setBoundProDocs([]);
    setSubThreads([]);
    setProjectApps([]);
    setThreadDescription(undefined);
    setParentThreadId(undefined);
    setParentThreadIsInternalMonologue(undefined);
    setIconImageUrl(undefined);

    if (!threadId) {
      setLayoutLoading(false);
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<GetThreadLayoutTypeResponse>(
        `/agents/threads/${threadId}/layout-type`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        setLayoutType(response.data.layoutType);
        setDiscussingObject(response.data.discussingObject);
        setDiscussingObjectMatch(response.data.discussingObjectMatch);
        setIsMainThread(response.data.isMainThread ?? false);
        setIsChannelThread(response.data.isChannelThread ?? false);
        setIsOnboardingThread(response.data.isOnboardingThread ?? false);
        setIsInboundEmailThread(response.data.isInboundEmailThread ?? false);
        setAgentType(response.data.agentType);
        setBoundProDocs(response.data.boundProDocs ?? []);
        setSubThreads(response.data.subThreads ?? []);
        setProjectApps(response.data.projectApps ?? []);
        setThreadDescription(response.data.threadDescription);
        setParentThreadId(response.data.parentThreadId);
        setParentThreadIsInternalMonologue(
          response.data.parentThreadIsInternalMonologue,
        );
        setIconImageUrl(response.data.iconImageUrl);
      }
    } catch (error) {
      console.error('Error fetching thread layout type:', error);
      setLayoutType('tab');
      setDiscussingObject(undefined);
      setDiscussingObjectMatch(undefined);
      setIsMainThread(false);
      setIsChannelThread(false);
      setIsOnboardingThread(false);
      setIsInboundEmailThread(false);
      setAgentType(undefined);
      setBoundProDocs([]);
      setSubThreads([]);
      setProjectApps([]);
      setThreadDescription(undefined);
      setParentThreadId(undefined);
      setParentThreadIsInternalMonologue(undefined);
      setIconImageUrl(undefined);
    } finally {
      setLayoutLoading(false);
    }
  }, [threadId, getAccessTokenSilently]);

  // Fetch layout type when threadId changes
  useEffect(() => {
    fetchLayoutType();
  }, [fetchLayoutType]);

  // Subscribe to Pusher event for discussing objects changes
  useEffect(() => {
    if (!threadId || !pusherContext?.pusherInstance) return;

    const channelName = threadChannel(PUSHER_ENV, threadId);
    const channel = pusherContext.pusherInstance.subscribe(channelName);

    const handleDiscussingObjectsChanged = () => {
      console.log('Discussing objects changed, refetching layout type');
      fetchLayoutType();
    };

    channel.bind(
      AgentEvents.THREAD_DISCUSSING_OBJECTS_CHANGED,
      handleDiscussingObjectsChanged,
    );

    return () => {
      channel.unbind(
        AgentEvents.THREAD_DISCUSSING_OBJECTS_CHANGED,
        handleDiscussingObjectsChanged,
      );
      // Don't unsubscribe here as other components may be using the channel
    };
  }, [threadId, pusherContext?.pusherInstance, fetchLayoutType]);

  // Subscribe to Pusher event for ProDoc binding changes (on company channel)
  useEffect(() => {
    if (!threadId || !pusherContext?.pusherChannel) {
      return;
    }

    const handleBindingChanged = (data: { threadId: string }) => {
      console.log('[ProDoc Binding] Received event:', data);
      if (data.threadId === threadId) {
        console.log(
          '[ProDoc Binding] ThreadId matches, refetching layout type',
        );
        fetchLayoutType();
      }
    };

    pusherContext.pusherChannel.bind(
      AgentEvents.THREAD_PRO_DOC_BINDING_CHANGED,
      handleBindingChanged,
    );

    return () => {
      pusherContext.pusherChannel?.unbind(
        AgentEvents.THREAD_PRO_DOC_BINDING_CHANGED,
        handleBindingChanged,
      );
    };
  }, [threadId, pusherContext?.pusherChannel, fetchLayoutType]);

  // Subscribe to Pusher event for ProDoc renamed (on company channel)
  useEffect(() => {
    if (!pusherContext?.pusherChannel) {
      return;
    }

    const handleProDocRenamed = (data: {
      threadId?: string;
      proDocId: string;
      newTitle: string;
    }) => {
      // Update the title directly in boundProDocs state if this ProDoc is bound
      setBoundProDocs((prev) => {
        const updated = prev.map((doc) =>
          doc.proDocId === data.proDocId
            ? { ...doc, title: data.newTitle }
            : doc,
        );
        // Only update if something changed
        return JSON.stringify(updated) !== JSON.stringify(prev)
          ? updated
          : prev;
      });
    };

    pusherContext.pusherChannel.bind(
      AgentEvents.PRO_DOC_RENAMED,
      handleProDocRenamed,
    );

    return () => {
      pusherContext.pusherChannel?.unbind(
        AgentEvents.PRO_DOC_RENAMED,
        handleProDocRenamed,
      );
    };
  }, [pusherContext?.pusherChannel]);

  // Subscribe to Pusher event for sub-thread updates
  useEffect(() => {
    if (!threadId || !pusherContext?.pusherInstance) return;

    const channelName = threadChannel(PUSHER_ENV, threadId);
    const channel = pusherContext.pusherInstance.subscribe(channelName);

    const handleSubThreadUpdate = (data: SubThreadUpdateEvent) => {
      console.log('[SubThread] Received update:', data);
      if (data.parentThreadId === threadId) {
        setSubThreads((prev) => {
          // Find and update or add the sub-thread
          const existingIndex = prev.findIndex(
            (st) => st.id === data.subThread.id,
          );
          if (existingIndex >= 0) {
            // Update existing
            const updated = [...prev];
            updated[existingIndex] = data.subThread;
            return updated;
          } else {
            // Add new
            return [data.subThread, ...prev];
          }
        });
      }
    };

    channel.bind('sub-thread-update', handleSubThreadUpdate);

    return () => {
      channel.unbind('sub-thread-update', handleSubThreadUpdate);
    };
  }, [threadId, pusherContext?.pusherInstance]);

  // Subscribe to Pusher events for project app changes (on company channel)
  useEffect(() => {
    if (!threadId || !pusherContext?.pusherChannel) return;

    const handleAppCreated = (data: ProjectAppCreatedEvent) => {
      if (data.threadId !== threadId) return;
      setProjectApps((prev) => {
        if (prev.some((a) => a.appId === data.app.id)) return prev;
        return [
          ...prev,
          {
            appId: data.app.id,
            name: data.app.name,
            slug: data.app.slug,
            description: data.app.description ?? undefined,
            blocks: [],
            views: [],
          },
        ];
      });
    };

    // When the PVA generates or updates a view, merge it into projectApps so the
    // tab bar reflects the new view immediately (no full layout refetch needed).
    const handleViewUpdated = (data: {
      threadId: string;
      appId: string;
      slug: string;
      name?: string;
      version: number;
    }) => {
      if (data.threadId !== threadId) return;
      setProjectApps((prev) =>
        prev.map((app) => {
          if (app.appId !== data.appId) return app;
          const existingViews = app.views || [];
          const idx = existingViews.findIndex((v) => v.slug === data.slug);
          const nextView = {
            slug: data.slug,
            name: data.name || existingViews[idx]?.name || data.slug,
            version: data.version,
          };
          const views =
            idx === -1
              ? [...existingViews, nextView]
              : existingViews.map((v, i) => (i === idx ? nextView : v));
          return { ...app, views };
        }),
      );
    };

    // When views are soft-deleted via adjustProjectApp, remove them from tabs.
    const handleViewDeleted = (data: {
      threadId: string;
      appId: string;
      slugs: string[];
    }) => {
      if (data.threadId !== threadId) return;
      setProjectApps((prev) =>
        prev.map((app) => {
          if (app.appId !== data.appId) return app;
          const views = (app.views || []).filter(
            (v) => !data.slugs.includes(v.slug),
          );
          return { ...app, views };
        }),
      );
    };

    pusherContext.pusherChannel.bind(
      AgentEvents.PROJECT_APP_CREATED,
      handleAppCreated,
    );
    pusherContext.pusherChannel.bind(
      AgentEvents.PROJECT_APP_VIEW_UPDATED,
      handleViewUpdated,
    );
    pusherContext.pusherChannel.bind(
      AgentEvents.PROJECT_APP_VIEW_DELETED,
      handleViewDeleted,
    );

    return () => {
      pusherContext.pusherChannel?.unbind(
        AgentEvents.PROJECT_APP_CREATED,
        handleAppCreated,
      );
      pusherContext.pusherChannel?.unbind(
        AgentEvents.PROJECT_APP_VIEW_UPDATED,
        handleViewUpdated,
      );
      pusherContext.pusherChannel?.unbind(
        AgentEvents.PROJECT_APP_VIEW_DELETED,
        handleViewDeleted,
      );
    };
  }, [threadId, pusherContext?.pusherChannel, fetchLayoutType]);

  // Listen for icon image ready on the user channel — the image is generated
  // async after the thread is created, so update iconImageUrl when it arrives.
  const userChannel = (pusherContext as any)?.userChannel ?? null;
  useEffect(() => {
    if (!userChannel || !threadDescription) return;

    const handleTodoImageReady = (data: TodoImageReadyEvent) => {
      if (!data.imageUrl || !data.description) return;
      if (data.description === threadDescription && !iconImageUrl) {
        setIconImageUrl(data.imageUrl);
      }
    };

    userChannel.bind(AgentEvents.TODO_IMAGE_READY, handleTodoImageReady);
    return () => {
      userChannel.unbind(AgentEvents.TODO_IMAGE_READY, handleTodoImageReady);
    };
  }, [userChannel, threadDescription, iconImageUrl]);

  // Listen for project tag assigned — fires after the LLM classifier picks a
  // tag + icon for the project. Merges projectTag/projectIcon into the
  // existing discussingObjectMatch so the canvas header icon updates live.
  useEffect(() => {
    if (!userChannel || !threadId) return;

    const handleTagAssigned = (data: {
      threadId: string;
      projectTag: string;
      projectIcon: string;
    }) => {
      if (data.threadId !== threadId) return;
      setDiscussingObjectMatch((prev) => ({
        ...prev,
        projectTag: data.projectTag,
        projectIcon: data.projectIcon,
      }));
    };

    userChannel.bind(
      AgentEvents.THREAD_DISCUSSING_OBJECTS_CHANGED,
      handleTagAssigned,
    );
    return () => {
      userChannel.unbind(
        AgentEvents.THREAD_DISCUSSING_OBJECTS_CHANGED,
        handleTagAssigned,
      );
    };
  }, [userChannel, threadId]);

  // Handle push notification navigation — close mobile nav when arriving
  // via a push click. TFA-specific URL handling (?tfa_request_id=...) lives
  // in TFAPusherListener at app level so it works regardless of route.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const fromNotification = searchParams.get('from_notification') === 'true';
    if (!fromNotification) return;

    closeMobileNav();

    const next = new URLSearchParams(searchParams);
    next.delete('from_notification');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, closeMobileNav]);

  // Auto-open canvas drawer on mobile when canvas assets arrive (not for app tabs)
  useEffect(() => {
    if (isMobile && hasCanvasAssets) {
      setCanvasDrawerOpen(true);
    }
  }, [isMobile, hasCanvasAssets, layoutType]);

  // Swipe gesture to open sidebar on mobile
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  useEffect(() => {
    if (!isMobile) return;

    // Check if running as PWA
    const isPWA =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;

    const EDGE_THRESHOLD = 30; // px from left edge to start swipe
    const SWIPE_MIN_DISTANCE = 50; // px to trigger sidebar open

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      // Only track swipes starting from left edge
      if (touch.clientX <= EDGE_THRESHOLD) {
        touchStartX.current = touch.clientX;
        touchStartY.current = touch.clientY;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      // In PWA mode, prevent default on horizontal swipes from left edge
      // This stops iOS from triggering the native back gesture
      if (!isPWA) return;
      if (touchStartX.current === null || touchStartY.current === null) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStartX.current;
      const deltaY = Math.abs(touch.clientY - touchStartY.current);

      // If swiping horizontally from left edge, prevent default
      if (deltaX > 10 && deltaX > deltaY) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (touchStartX.current === null || touchStartY.current === null) return;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartX.current;
      const deltaY = Math.abs(touch.clientY - touchStartY.current);

      // Swipe right from left edge, more horizontal than vertical
      if (deltaX > SWIPE_MIN_DISTANCE && deltaX > deltaY) {
        // If canvas drawer is open, close it first; otherwise open sidebar
        if (canvasDrawerOpen) {
          setCanvasDrawerOpen(false);
        } else {
          openMobileNav();
        }
      }

      touchStartX.current = null;
      touchStartY.current = null;
    };

    document.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    // passive: false required to call preventDefault() on touchmove
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isMobile, canvasDrawerOpen]);

  // Calculate if we need right margin for sub-thread view
  // Only applies when sub-thread view is open AND there are no tabs (hasCanvasAssets = false)
  const needsSubThreadMargin = isSubThreadViewOpen && !hasCanvasAssets;

  // Derive conversation type: 'autopilot' | 'project' | 'task'
  const conversationType = useMemo(() => {
    if (discussingObject === 'Autopilot') return 'autopilot' as const;
    if (parentThreadId) return 'task' as const;
    return 'project' as const;
  }, [discussingObject, parentThreadId]);

  const cameFromQuickChat = searchParams.get('from') === 'quickchat';
  const cameFromTasks = searchParams.get('from') === 'tasks';
  // Projects intentionally have no breadcrumb — there's no dedicated
  // project list page (projects live in the side-nav drawer), so a
  // "Back to Project List" link would be misleading.
  const breadcrumbLabel = useMemo(() => {
    if (cameFromQuickChat) return 'Back to Chat';
    switch (conversationType) {
      case 'autopilot':
        return 'Back to Autopilot List';
      case 'task':
        return 'Back to Task List';
      case 'project':
        return null;
    }
  }, [conversationType, cameFromQuickChat]);

  const breadcrumbHref = cameFromTasks ? '/tasks' : '/';

  return (
    <div
      className={`chat-interface inset-white-backed${!hasCanvasAssets ? ' no-assets' : ''}`}
      style={needsSubThreadMargin ? { paddingRight: '344px' } : undefined}
    >
      <div className='chat-interface-main'>
        {/* Top bar: Home + All projects */}
        <div className='chat-top-bar'>
          <button
            type='button'
            className='chat-top-bar-btn'
            onClick={() => navigate(cameFromTasks ? '/tasks' : '/')}
          >
            <ArrowLeftIcon />
            <span>{cameFromTasks ? 'Tasks' : 'Home'}</span>
          </button>
          {!cameFromTasks && (
            <>
              <span className='chat-top-bar-divider' aria-hidden='true' />
              <button
                ref={allProjectsBtnRef}
                type='button'
                className='chat-top-bar-btn'
                onClick={() => setProjectsDrawerOpen((v) => !v)}
              >
                <ProjectsFolderIcon />
                <span>All projects</span>
              </button>
            </>
          )}
        </div>

        <ProjectsDrawer
          open={projectsDrawerOpen}
          agentId={activeAgent?.id || ''}
          agentName={activeAgent?.nickname || activeAgent?.name}
          agentImageUrl={activeAgent?.image_url}
          agentType={activeAgent?.agent_type as string}
          onClose={() => setProjectsDrawerOpen(false)}
          toggleRef={allProjectsBtnRef}
        />

        {/* Breadcrumbs */}
        {!layoutLoading && breadcrumbLabel && (
          <div className='chat-breadcrumbs'>
            <button
              className='breadcrumb-back'
              onClick={() => navigate(breadcrumbHref)}
            >
              <ArrowLeftIcon />
              <span>{breadcrumbLabel}</span>
            </button>
          </div>
        )}

        {/* Header shown here only when no canvas assets (or always on mobile) */}
        {(isMobile || !hasCanvasAssets) && (
          <ChatHeader
            threadId={threadId}
            isMobile={isMobile}
            onToggleSidebar={openMobileNav}
            showCanvasToggle={
              hasCanvasAssets ||
              (layoutType === 'app' && !!discussingObjectMatch)
            }
            onToggleCanvas={() => setCanvasDrawerOpen(true)}
          />
        )}

        <div
          className={`chat-layout${conversationType === 'task' ? ' flipped' : ''}`}
        >
          {threadId && (
            <div
              className={`chat-canvas-container${!hasCanvasAssets ? ' no-canvas-assets' : ''}`}
            >
              <StatusPillHiddenProvider hidden={!!parentThreadId}>
                <ProjectAppLoadingProvider initialAppStatuses={projectApps}>
                  <ChatCanvas
                    ref={chatCanvasRef}
                    layoutType={layoutType}
                    discussingObject={discussingObject}
                    discussingObjectMatch={discussingObjectMatch}
                    isMainThread={isMainThread}
                    isChannelThread={isChannelThread}
                    isOnboardingThread={isOnboardingThread}
                    isInboundEmailThread={isInboundEmailThread}
                    agentType={agentType}
                    boundProDocs={boundProDocs}
                    subThreads={subThreads}
                    projectApps={projectApps}
                    threadDescription={threadDescription}
                    layoutLoading={layoutLoading}
                    onHasAssets={setHasCanvasAssets}
                    iconImageUrl={iconImageUrl}
                  />
                </ProjectAppLoadingProvider>
              </StatusPillHiddenProvider>
            </div>
          )}
        </div>
      </div>

      {/* Task threads have no chat sidebar — only the canvas. The
          corresponding chat (the parent "Discuss:" thread) is reachable
          from the breadcrumb / browser back. */}
      {conversationType !== 'task' && (
        <div
          className={`chat-sidebar-container${isCollapsed ? ' chat-collapsed' : ''}${!hasCanvasAssets ? ' no-canvas-assets' : ''}`}
        >
          <div className='chat-panel-header'>
            <div className='chat-panel-header-left'>
              {activeAgent?.image_url && (
                <div className='chat-panel-header-avatar'>
                  <img
                    src={activeAgent.image_url}
                    alt={activeAgent.nickname || activeAgent.name || ''}
                  />
                </div>
              )}
              <span className='chat-panel-header-name'>
                {activeAgent?.nickname || activeAgent?.name}
              </span>
            </div>
            <div className='chat-panel-header-actions'>
              {threadCall?.canStartCall && (
                <button
                  type='button'
                  className='chat-panel-call-btn'
                  onClick={threadCall.startCall}
                  aria-label={`Call ${activeAgent?.nickname || activeAgent?.name || 'agent'}`}
                  title={`Call ${activeAgent?.nickname || activeAgent?.name || 'agent'}`}
                >
                  <CallingIcon />
                </button>
              )}
              {!isMobile && (
                <button
                  type='button'
                  className='chat-panel-collapse-btn'
                  onClick={collapseSidebar}
                  aria-label='Collapse chat'
                >
                  <AlignRightIcon />
                </button>
              )}
            </div>
          </div>
          <div className='chat-panel-body'>
            {isMobile && conversationType === 'project' && (
              <button
                className='sidebar-mobile-back'
                onClick={() => setCanvasDrawerOpen(true)}
              >
                <ArrowLeftIcon />
                <span>Back</span>
              </button>
            )}
            <ChatSidebar
              layoutType={layoutType}
              hasCanvasAssets={hasCanvasAssets}
              threadId={threadId}
              parentThreadId={parentThreadId}
              parentThreadIsInternalMonologue={parentThreadIsInternalMonologue}
              onOpenCanvas={() => setCanvasDrawerOpen(true)}
            />
            <SidebarDrawer
              onThreadSelect={(threadId, agentId, readMarker) => {
                // Navigate to the thread
                const params = new URLSearchParams();
                if (agentId) params.set('agent_id', agentId);
                params.set('thread_id', threadId);
                window.location.search = params.toString();
              }}
            />
          </div>
        </div>
      )}

      {/* Floating pill shown when the chat panel is collapsed */}
      {conversationType !== 'task' && isCollapsed && !isMobile && (
        <button
          type='button'
          className='chat-collapsed-pill'
          onClick={expandSidebar}
          aria-label='Expand chat'
        >
          {activeAgent?.image_url && (
            <div className='chat-panel-header-avatar'>
              <img
                src={activeAgent.image_url}
                alt={activeAgent.nickname || activeAgent.name || ''}
              />
            </div>
          )}
          <span className='chat-panel-header-name'>
            {activeAgent?.nickname || activeAgent?.name}
          </span>
          <ExpandIcon className='chat-collapsed-pill-expand' />
        </button>
      )}

      {/* Mobile canvas drawer - always render on mobile so it can be opened when confirmations create assets */}
      {isMobile && (
        <>
          <div
            className={`mobile-canvas-backdrop ${canvasDrawerOpen ? 'open' : ''}`}
            onClick={() => setCanvasDrawerOpen(false)}
          />
          <div
            className={`mobile-canvas-drawer ${canvasDrawerOpen ? 'open' : ''}`}
          >
            <div className='mobile-canvas-drawer-content'>
              {threadId && (
                <StatusPillHiddenProvider hidden={!!parentThreadId}>
                  <ProjectAppLoadingProvider initialAppStatuses={projectApps}>
                    <ChatCanvas
                      ref={chatCanvasRef}
                      layoutType={layoutType}
                      discussingObject={discussingObject}
                      discussingObjectMatch={discussingObjectMatch}
                      isMainThread={isMainThread}
                      isOnboardingThread={isOnboardingThread}
                      isInboundEmailThread={isInboundEmailThread}
                      agentType={agentType}
                      boundProDocs={boundProDocs}
                      subThreads={subThreads}
                      projectApps={projectApps}
                      threadDescription={threadDescription}
                      layoutLoading={layoutLoading}
                      onHasAssets={setHasCanvasAssets}
                      onMobileBack={() => setCanvasDrawerOpen(false)}
                      onMobileOpen={() => setCanvasDrawerOpen(true)}
                      hideMobileChatButton
                      iconImageUrl={iconImageUrl}
                    />
                  </ProjectAppLoadingProvider>
                </StatusPillHiddenProvider>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const ChatInterface: FC = () => {
  const [searchParams] = useSearchParams();
  const threadId = searchParams.get('thread_id');

  return (
    <SidebarDrawerProvider>
      <ChatCanvasProvider>
        <ChatSidebarCollapseProvider threadId={threadId}>
          <SubThreadViewProvider>
            <ConfirmationsProvider threadId={threadId}>
              <HeroImageProvider>
                <ThreadCallProvider>
                  <ChatInterfaceContent threadId={threadId} />
                </ThreadCallProvider>
                {/* <SubThreadView threadId={threadId} /> */}
              </HeroImageProvider>
            </ConfirmationsProvider>
          </SubThreadViewProvider>
        </ChatSidebarCollapseProvider>
      </ChatCanvasProvider>
    </SidebarDrawerProvider>
  );
};

export default ChatInterface;
